import React, { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import './NetworkBanner.css';

// Resolve the Network plugin lazily so the web build still works without it.
let Network = null;
try {
  // eslint-disable-next-line global-require
  Network = require('@capacitor/network').Network;
} catch (e) {
  Network = null;
}

/**
 * Persistent connectivity banner. Shows "No internet connection" while the
 * device is offline (so the user understands why things aren't loading), and a
 * brief "Back online" confirmation when connectivity returns. Works on both the
 * native (Capacitor Network) and web (navigator online/offline) layers.
 */
export default function NetworkBanner() {
  const [online, setOnline] = useState(true);
  const [visible, setVisible] = useState(false);
  const wasOnline = useRef(true);
  const hideTimer = useRef(null);

  useEffect(() => {
    const clearHide = () => {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
    };

    const apply = (isOnline) => {
      const was = wasOnline.current;
      if (was === isOnline) return;
      wasOnline.current = isOnline;
      setOnline(isOnline);

      if (!isOnline) {
        // Went offline — show and keep showing.
        clearHide();
        setVisible(true);
      } else {
        // Reconnected — confirm briefly, then auto-hide.
        setVisible(true);
        clearHide();
        hideTimer.current = setTimeout(() => setVisible(false), 2600);
      }
    };

    let removeNative;
    (async () => {
      try {
        if (Network && Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
          const status = await Network.getStatus();
          wasOnline.current = status.connected;
          setOnline(status.connected);
          if (!status.connected) setVisible(true);
          const handle = await Network.addListener('networkStatusChange', (s) => apply(s.connected));
          removeNative = () => handle.remove();
        } else if (typeof navigator !== 'undefined') {
          wasOnline.current = navigator.onLine;
          setOnline(navigator.onLine);
          if (!navigator.onLine) setVisible(true);
        }
      } catch (e) {
        /* no-op: leave optimistic online state */
      }
    })();

    const handleOnline = () => apply(true);
    const handleOffline = () => apply(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearHide();
      if (removeNative) removeNative();
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      className={`net-banner ${online ? 'net-banner--ok' : 'net-banner--off'}`}
      role="status"
      aria-live="polite"
    >
      <span className="net-banner__dot" aria-hidden="true" />
      <span className="net-banner__text">
        {online
          ? 'Back online'
          : 'No internet connection — check your Wi‑Fi or mobile data'}
      </span>
    </div>
  );
}
